import { useRedisWritesBlocked } from '@/apis/call';
import { GetListParameters, getList } from '@/apis/lists';
import { environment } from '@/config';
import { CallingStatusValues } from '@/constants/callingStatus';
import { SupporterList } from '@/types';
import { createBaseContactUrl } from '@/util';
import { domainCodeAtom, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { autoProgressInCallingViewAtom } from '@hooks/useApp';
import { SelectChangeEvent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { computeCallingNavigationIndices } from './callingTabNavigation';
import {
  CALLING_STATUS_SAVE_ERROR,
  persistCallingStatusChange,
} from './persistCallingStatusChange';
import { useUpdateCallingStatus } from './hooks';
import { openContactInRocket, saveContactFormInRocket } from './rocketIntegration';
import { useRocketReadyForRequests } from './rocketIntegration/useRocketReadyForRequests';
import { useCheckRocketFormStatus } from './UnsavedRocketChangesGuard';

export type UseCallingTabParams = {
  callingViewContactIndex: number;
  setCallingViewContactIndex: Dispatch<SetStateAction<number>>;
  queryParameters: Omit<GetListParameters, 'token' | 'domainCode'>;
  list: SupporterList | undefined;
  pageNo: number;
  enterCallingByTableClick: boolean;
  setEnterCallingByTableClick: Dispatch<SetStateAction<boolean>>;
  viewContactedChecked: boolean;
  listsAreLoadingAndOrFetching: boolean;
};

export function useCallingTab({
  callingViewContactIndex,
  setCallingViewContactIndex,
  queryParameters,
  list,
  pageNo,
  enterCallingByTableClick,
  setEnterCallingByTableClick,
  viewContactedChecked,
  listsAreLoadingAndOrFetching,
}: UseCallingTabParams) {
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [warningButtonName, setWarningButtonName] = useState('Save');
  const [currentlyAssignedUser, setCurrentlyAssignedUser] = useState('');
  const { checkRocketFormStatus, setIsEnabled: setEnableRocketStatusChecks } =
    useCheckRocketFormStatus();

  const isDev = ['test', 'local', 'dev'].includes(environment);
  const domainCode = useAtomValue(domainCodeAtom);
  const theIFrameURL = createBaseContactUrl(domainCode ?? 'ag', isDev);
  const { getAccessTokenSilently, user } = useAuth0();
  const userSub = user?.sub ?? '';
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const ourAssignedToSub = queryParameters.assignedToSub || '';
  const ourSortColumn = queryParameters.sortColumn || '';
  const ourSortOrder = queryParameters.sortOrder || '';

  const { data, isLoading } = useQuery({
    enabled: callingViewContactIndex !== -1,
    queryKey: ['calling', callingViewContactIndex, ourAssignedToSub, ourSortColumn, ourSortOrder],
    queryFn: async () => {
      const token = getAccessTokenSilently();
      const { assignedToSub, listId, sortOrder, sortColumn, filters } = queryParameters;
      return getList({
        token: await token,
        listId,
        limit: 1,
        offset: callingViewContactIndex,
        domainCode,
        sortColumn,
        sortOrder,
        assignedToSub,
        filters,
      });
    },
    refetchOnWindowFocus: false,
  });

  const callingViewContactIndexMod =
    list && callingViewContactIndex !== -1 ? callingViewContactIndex % list.limit : null;
  const callingViewContactIndexRmd =
    list && callingViewContactIndexMod !== null
      ? callingViewContactIndex - callingViewContactIndexMod
      : null;

  const currentCallingStatus =
    list &&
    callingViewContactIndexMod !== null &&
    list.contactRecords &&
    list.contactRecords.length &&
    pageNo * list.limit === callingViewContactIndexRmd &&
    callingViewContactIndexMod < list.contactRecords.length
      ? list.contactRecords[callingViewContactIndexMod].callingStatus
      : '';

  const [callingStatus, setCallingStatus] = useState(list ? currentCallingStatus : '');
  const [savingCallingStatus, setSavingCallingStatus] = useState(false);
  const [skippingCallingStatus, setSkippingCallingStatus] = useState(false);

  const { previousIndex, previousStatus, nextIndex, nextStatus, nextUncheckedIndex } = useMemo(
    () =>
      computeCallingNavigationIndices({
        list,
        callingViewContactIndex,
        callingViewContactIndexMod,
        callingViewContactIndexRmd,
        pageNo,
        viewContactedChecked,
      }),
    [
      list,
      callingViewContactIndex,
      callingViewContactIndexMod,
      callingViewContactIndexRmd,
      pageNo,
      viewContactedChecked,
    ]
  );

  const listCompleted =
    list && list.firstUncalledIndex === null && !viewContactedChecked && !enterCallingByTableClick;
  const showCompleteMessage = Boolean(
    listCompleted && !savingCallingStatus && !skippingCallingStatus
  );
  const rocketIFrameMounted = !showCompleteMessage;
  const rocketReadyForRequests = useRocketReadyForRequests(rocketIFrameMounted);
  const contactId =
    data?.contactRecords && data?.contactRecords.length > 0 ? data.contactRecords[0].id : undefined;
  const listId = list?.id;

  const { mutateAsync: mutateUpdateCallingStatus } = useUpdateCallingStatus({ contactId, listId });

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setCallingStatus(event.target.value);
  };

  const handleNavigateBefore = async () => {
    const safeToNavigate = await checkRocketFormStatus();
    if (safeToNavigate && previousIndex !== null && previousStatus !== null) {
      setCallingViewContactIndex(previousIndex);
      setCallingStatus(previousStatus);
    }
  };

  const isAutoProgressPreferenceEnabled = useAtomValue(autoProgressInCallingViewAtom);

  const handleNavigateNext = async (bypassPreferenceCheck?: boolean) => {
    if (!bypassPreferenceCheck && !isAutoProgressPreferenceEnabled) return;
    const safeToNavigate = await checkRocketFormStatus();
    if (safeToNavigate && nextIndex !== null && nextStatus !== null) {
      setCallingViewContactIndex(nextIndex);
      setCallingStatus(nextStatus);
    }
  };

  const handleUpdateCallingStatus = async (newCallingStatus: string, reassignSub = false) => {
    if (!list) return;

    await mutateUpdateCallingStatus(
      { newCallingStatus, reassignSub },
      {
        onSuccess: () => {
          if (nextUncheckedIndex === null && list && newCallingStatus !== CallingStatusValues[0]) {
            if (
              list.firstUncalledIndex !== null &&
              list.firstUncalledIndex !== callingViewContactIndex
            ) {
              setCallingViewContactIndex(list.firstUncalledIndex);
              setCallingStatus(CallingStatusValues[0]);
            } else {
              setCallingStatus(newCallingStatus);
              enqueueSuccessNotification('List complete');
              setEnterCallingByTableClick(false);
            }
          }
        },
      }
    );
  };

  const reportCallingSaveError = () => enqueueErrorNotification(CALLING_STATUS_SAVE_ERROR);

  const saveAssignedStatus = async (status: string, reassignSub = false) => {
    const saved = await persistCallingStatusChange({
      update: () => handleUpdateCallingStatus(status, reassignSub),
      saveRocketForm: saveContactFormInRocket,
      onError: reportCallingSaveError,
    });
    if (saved && nextUncheckedIndex !== null) await handleNavigateNext();
    return saved;
  };

  const handleSkip = async () => {
    if (writesBlocked) return;
    setWarningButtonName('Skip');
    if (data && data.contactRecords.length > 0) {
      if (data.contactRecords[0].assignedToSub === userSub) {
        await saveAssignedStatus('Skipped');
      } else {
        setCurrentlyAssignedUser(data.contactRecords[0].assigneeName || '');
        setIsWarningDialogOpen(true);
      }
    }
  };

  const handleSave = async () => {
    if (writesBlocked) return;
    setWarningButtonName(currentCallingStatus === CallingStatusValues[0] ? 'Save' : 'Update');

    if (data && data.contactRecords.length > 0) {
      if (data.contactRecords[0].assignedToSub === userSub) {
        await saveAssignedStatus(callingStatus);
      } else {
        setCurrentlyAssignedUser(data.contactRecords[0].assigneeName || '');
        setIsWarningDialogOpen(true);
      }
    }
  };

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-chain-state-updates -- seed calling status from the loaded list */
  useEffect(() => {
    if (list && !callingStatus && list.count) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seed calling status from the loaded list
      setCallingStatus(currentCallingStatus);
    }
  }, [list, callingStatus, setCallingStatus, currentCallingStatus]);

  const contactName =
    data?.contactRecords && data?.contactRecords.length > 0
      ? data.contactRecords[0].preferredName + ' ' + data.contactRecords[0].lastName
      : undefined;

  useEffect(() => {
    setEnableRocketStatusChecks(rocketReadyForRequests);
  }, [rocketReadyForRequests, setEnableRocketStatusChecks]);

  useEffect(() => {
    if (rocketReadyForRequests && contactId && rocketIFrameMounted) {
      openContactInRocket(contactId);
    }
  }, [rocketReadyForRequests, contactId, rocketIFrameMounted]);

  // Use isLoading (no data yet), not isFetching — recovery invalidates must not
  // freeze the calling controls while existing contact data remains on screen.
  const debounceDisable =
    isLoading || listsAreLoadingAndOrFetching || savingCallingStatus || skippingCallingStatus;

  const handleWarningDialogConfirm = async () => {
    if (writesBlocked) return;
    const isSkip = warningButtonName === 'Skip';
    if (isSkip) setSkippingCallingStatus(true);
    else setSavingCallingStatus(true);
    try {
      const saved = await saveAssignedStatus(isSkip ? 'Skipped' : callingStatus, true);
      if (saved) setIsWarningDialogOpen(false);
    } finally {
      setSkippingCallingStatus(false);
      setSavingCallingStatus(false);
    }
  };

  return {
    theIFrameURL,
    listCompleted,
    showCompleteMessage,
    callingViewContactIndex,
    list,
    debounceDisable,
    previousIndex,
    nextIndex,
    handleNavigateBefore,
    handleNavigateNext,
    handleSkip,
    handleSave,
    callingStatus,
    handleChangeStatus,
    currentCallingStatus,
    savingCallingStatus,
    skippingCallingStatus,
    setSavingCallingStatus,
    setSkippingCallingStatus,
    isWarningDialogOpen,
    setIsWarningDialogOpen,
    currentlyAssignedUser,
    warningButtonName,
    handleWarningDialogConfirm,
    contactName,
    contactId,
  };
}
